import { z } from "zod";

import * as DbEnum from "../../../../generated/db-enum";
import { enumReadSchema } from "../../../../shared/core/enum-read.schema";

/**
 * The READ side of the Vibe deployment enums: what a console or a CLI parses
 * off a response, as opposed to what the backend may emit.
 *
 * Its own module because both `deployments.schemas.ts` and
 * `approvals.schemas.ts` read these fields, and the first already imports the
 * second — so the read schemas live where neither has to import the other.
 *
 * Every schema here exists because a value the backend learned first used to
 * fail the WHOLE response in an older console — see `enumReadSchema` for the
 * mechanism and for which fields may use it. Each one below has an honest
 * rendering for a value it does not list; the deployment and build-job STATUS
 * do not, and are deliberately absent (see `status-read.schemas.ts`).
 */

/**
 * What kicked a deployment off. An unlisted source is still a true sentence —
 * "triggered by <source>" — and nothing branches on it except grouping, which a
 * raw string groups correctly.
 */
export const VibeTriggerSourceReadSchema = enumReadSchema(DbEnum.VIBE_TRIGGER_SOURCE_VALUES);
export type VibeTriggerSourceReadValue = z.infer<typeof VibeTriggerSourceReadSchema>;

/**
 * Which build strategy the executor used. Display only: an unlisted builder
 * renders as its own name.
 */
export const VibeBuilderKindReadSchema = enumReadSchema(DbEnum.VIBE_BUILDER_KIND_VALUES);
export type VibeBuilderKindReadValue = z.infer<typeof VibeBuilderKindReadSchema>;

/**
 * The internal blue/green slot. No console or CLI surface renders it, so an
 * unlisted slot has nothing to be wrong about — and must not fail the read
 * that carries everything a surface DOES render.
 */
export const VibeDeploymentColorReadSchema = enumReadSchema(DbEnum.VIBE_DEPLOYMENT_COLOR_VALUES);
export type VibeDeploymentColorReadValue = z.infer<typeof VibeDeploymentColorReadSchema>;
